import React from "react";
import styled from "styled-components";
import { Modal } from "../Modal";
import { Spacer } from "../Spacer";
import { Text } from "../Text";
import { useEvent } from "../hooks/useEvent";
import { style, mediaQueries } from "../style";
import { bigWarning } from "../utils/warning";
import { CreatePageAsideCard } from "./CreatePageAsideCard";
import { CreatePageAsideSection } from "./CreatePageAsideSection";
import { CreatePageMainSection } from "./CreatePageMainSection";

type AsideCardProp = { label: string; imageUrl?: string } | string;
type AsideCardData =
  | { type: "with-image"; label: string; imageUrl: string }
  | { type: "without-image"; label: string; imageUrl?: undefined }
  | { type: "none" };

type Props = {
  title: string;
  mobileAsideCard?: AsideCardProp;
};

type ContainerProps = {
  $compress: boolean;
};

const Container = styled.div<ContainerProps>`
  margin-left: auto;
  margin-right: auto;

  ${(props) => {
    if (props.$compress) {
      return {
        maxWidth: 600,
      };
    } else {
      return {
        maxWidth: 1000,
      };
    }
  }}
`;

type InnerContainerProps = {
  $compress: boolean;
};

const InnerContainer = styled.div<InnerContainerProps>`
  all: unset;

  margin: ${(props) => (props.$compress ? style.vars.space[5] : undefined)};
`;

const PageTitle = styled(Text).attrs({ type: "h1" })`
  margin-bottom: 0;
`;

const LayoutContainer = styled.div`
  all: unset;
  display: flex;
  column-gap: ${style.vars.space[10]};
`;

const MainWrapper = styled.div`
  all: unset;
  flex-basis: 60%;
  flex-grow: 3;
`;

const AsideWrapper = styled.div`
  all: unset;
  flex-basis: 40%;
  flex-shrink: 2;
  max-width: 400px;
`;

const ModalContentContainer = styled.div`
  all: unset;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const normalizeMobileAsideCard = (mobileAsideCard?: AsideCardProp): AsideCardData => {
  if (!mobileAsideCard) {
    return { type: "none" };
  }

  if (typeof mobileAsideCard === "string") {
    return { type: "without-image", label: mobileAsideCard };
  }

  if (mobileAsideCard.imageUrl === undefined) {
    return { type: "without-image", label: mobileAsideCard.label };
  }

  return {
    type: "with-image",
    label: mobileAsideCard.label,
    imageUrl: mobileAsideCard.imageUrl,
  };
};

export const CreatePageBody: React.FC<Props> = (props) => {
  const { title } = props;
  let asideCardData = normalizeMobileAsideCard(props.mobileAsideCard);

  const mediumMax = mediaQueries.mediumMax.useMatchesBreakpoint();
  const smallMax = mediaQueries.smallMax.useMatchesBreakpoint();
  const [modalOpen, setModalOpen] = React.useState(false);

  const asideCardRef = React.useRef<{ focus(): void }>({
    focus() {
      /* nothing */
    },
  });

  const openModal = useEvent(() => setModalOpen(true));

  const closeModal = useEvent(() => {
    setModalOpen(false);
    asideCardRef.current.focus();
  });

  /**
   * Iterate through all children in order to pluck out the main and aside sections.
   * If the aside does not exist, or we are on mobile, we display these nodes in different places.
   * Additionally, we want to warn if any children are unexpected.
   */
  let main: React.ReactNode;
  let aside: React.ReactNode;

  React.Children.forEach(props.children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === CreatePageMainSection) {
        if (main && process.env.NODE_ENV !== "production") {
          bigWarning(
            `CreatePage.Main used multiple times on CreatePage.Body. Only one Main section is allowed at once. The second element is ignored.`,
            "CreatePageBody-TooManyMains"
          );
          return;
        }

        main = child;
        return;
      } else if (child.type === CreatePageAsideSection) {
        if (aside && process.env.NODE_ENV !== "production") {
          bigWarning(
            `CreatePage.Aside used multiple times on CreatePage.Body. Only one Aside section is allowed at once. The second element is ignored.`,
            "CreatePageBody-TooManyAsides"
          );
          return;
        }

        aside = child;
      } else if (process.env.NODE_ENV !== "production") {
        bigWarning(
          `CreatePage.Body expects children to be CreatePage.Main, CreatePage.Aside and nothing else, but included a "${child.type}". This element is ignored.`,
          "CreatePageBody-UnknownReactElements"
        );
      }
    } else if (process.env.NODE_ENV !== "production") {
      bigWarning(
        `CreatePage.Body expects children to be CreatePage.Main, CreatePage.Aside and nothing else, but included a "${typeof child}". This element is ignored.`,
        "CreatePageBody-UnknownChild"
      );
    }
  });

  if (!main && process.env.NODE_ENV !== "production") {
    bigWarning(`CreatePage.Body expected a CreatePage.Main as a direct child.`, "CreatePageBody-NoMain");
  }

  if (!aside && asideCardData.type !== "none") {
    asideCardData = { type: "none" };

    if (process.env.NODE_ENV !== "production") {
      bigWarning(
        `CreatePage.Body included "mobileAsideCard" prop, but could not find a CreatePage.Aside as a child.`,
        "CreatePageBody-DataButNotAside"
      );
    }
  }

  return (
    <Container $compress={mediumMax}>
      <InnerContainer $compress={smallMax}>
        <PageTitle>{title}</PageTitle>
        <LayoutContainer>
          <MainWrapper>
            {mediumMax && aside && (
              <>
                <Spacer space={8} />
                {asideCardData.type !== "none" && (
                  <CreatePageAsideCard
                    ref={asideCardRef}
                    label={asideCardData.label}
                    imageUrl={asideCardData.imageUrl}
                    onClick={openModal}
                  />
                )}
                <Modal size="fullPage" title={title} open={modalOpen} onClose={closeModal} stickyHeader hideButtons>
                  <ModalContentContainer>
                    <AsideWrapper>{aside}</AsideWrapper>
                  </ModalContentContainer>
                </Modal>
              </>
            )}
            <Spacer space={8} />
            {main}
          </MainWrapper>
          {aside && !mediumMax && <AsideWrapper>{aside}</AsideWrapper>}
        </LayoutContainer>
      </InnerContainer>
    </Container>
  );
};

CreatePageBody.displayName = "CreatePageBody";
